import React from 'react';
import type { UseFormReturn } from 'react-hook-form';
import type { AddressList } from '@qbe/dfc-services';
import { AddressMethod, AddressSearchValue } from '@qbe/dfc-smart-ui';
import { processAddressChange } from 'components/AddressSearch/addressChangeHelpers';
import { ADDRESS_FIELD_MAPS } from 'components/AddressSearch/addressHelpers';
import { AddressType } from 'components/AddressSearch/addressTypes';
import type { FormDataType } from 'contexts/FormProvider';
import useResolvedLookupAddress from './useResolvedLookupAddress';

const useAddressChangeHandler = (type: AddressType, form: UseFormReturn<FormDataType>) => {
  const { getValues, setValue } = form;

  const getResolvedLookupAddress = useResolvedLookupAddress(type, form);

  const handleAddressChange = React.useCallback(
    (
      addressList: AddressList | null,
      structured: AddressSearchValue | null,
      _fieldName: string,
      method: AddressMethod
    ) => {
      const fieldMap = ADDRESS_FIELD_MAPS[type];
      const resolvedLookupAddress = getResolvedLookupAddress();

      const data = processAddressChange({
        type,
        addressList,
        structured,
        method,
        resolveManualAddress: {
          hasLookupSelection: !!getValues(fieldMap.addressObject),
          resolvedLookupAddress,
        },
      });

      if (data.applyChanges) {
        const options = { shouldValidate: true, shouldDirty: true };

        setValue(fieldMap.address, data.address as any, options);
        setValue(fieldMap.addressObject, data.addressObject as any, options);
        setValue(fieldMap.streetAddress, data.streetAddress as any, options);
        setValue(fieldMap.suburb, data.suburb as any, options);
        setValue(fieldMap.state, data.state as any, options);
        setValue(fieldMap.postcode, data.postcode as any, options);
        setValue(fieldMap.country, data.country as any, options);
        setValue(fieldMap.isAddressValid, data.isAddressValid as any, options);
        setValue(fieldMap.isManualAddress, data.isManualAddress as any, options);

        if (fieldMap.isManualAddressValid && data.isManualAddressValid !== undefined) {
          setValue(fieldMap.isManualAddressValid, data.isManualAddressValid as any, options);
        }
      }

      return data.shouldRemount;
    },
    [type, getValues, setValue, getResolvedLookupAddress]
  );

  return handleAddressChange;
};

export default useAddressChangeHandler;
